import React, {
  useContext,
  createContext,
  useState,
  ChangeEvent,
  useEffect,
} from 'react';
import {action, makeAutoObservable} from 'mobx';
import {observer} from 'mobx-react-lite';
import * as yup from 'yup';
import styled from 'styled-web';

const FormContext = createContext<FormState | null>(null);

const FormSchema = yup
  .object()
  .shape({
    anrede: yup.string().required(),
    name: yup.string().required(),
    street: yup.string().required(),
    zip: yup.string().required(),
    prospekte: yup.array().defined().of(yup.string().required()),
  })
  .defined();

interface TSFormSchema {
  anrede: string;
  name: string;
  street: string;
  zip: string;
  prospekte: string[];
}

class FormState {
  values: TSFormSchema;

  constructor(defaultValues: TSFormSchema) {
    this.values = defaultValues;
    makeAutoObservable(this);
  }

  async submit() {
    try {
      FormSchema.validateSync(this.values);
    } catch(err) {
      console.error(err);
      alert('Mindestens ein Feld wurde nicht ausgefüllt. Bitte überprüfen Sie Ihre Angaben.');
      return;
    }

    try {
      const resp = await fetch('/prospekt-api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(this.values),
      });

      const result = await resp.json();
      if (result.success) {
        alert('Ihre Prospektbestellung wurde erfolgreich übermittelt.');
      } else {
        alert('Es ist leider ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.');
      }
    } catch (err) {
      console.error(err);
    }
  }
}

interface FormFieldProps {
  label: string;
  name: {
    [key in keyof TSFormSchema]: TSFormSchema[key] extends string ? key : never;
  }[keyof TSFormSchema];
}

const FormField = observer((props: FormFieldProps) => {
  const formState = useContext(FormContext);

  if (typeof formState?.values[props.name] !== 'string') {
    throw new Error(`Invalid type detected for field ${props.name}!`);
  }

  return (
    <div className="form-group">
      <label className="control-label" htmlFor={props.name}>
        {props.label}
      </label>
      <input
        type="text"
        className="form-control"
        {...props}
        value={formState?.values[props.name]}
        onChange={action((e: ChangeEvent<HTMLInputElement>) => {
          if (formState !== null) {
            formState.values[props.name] = e.target.value;
          }
        })}
      />
    </div>
  );
});

interface CheckboxProps {
  value: string;
  name: {
    [key in keyof TSFormSchema]: TSFormSchema[key] extends string[]
      ? key
      : never;
  }[keyof TSFormSchema];
  children: React.ReactNode[] | React.ReactNode;
}

const Checkbox = observer(
  (props: CheckboxProps): React.ReactElement => {
    const state = useContext(FormContext);
    const values = state?.values[props.name];

    return (
      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={values ? values.indexOf(props.value) > -1 : false}
            onChange={action((e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.checked) {
                if (values && values.indexOf(props.value) < 0) {
                  values.push(props.value);
                }
              } else if (values) {
                const idx = values.indexOf(props.value);
                if (idx > -1) {
                  values.splice(idx, 1);
                }
              }
            })}
          />
          {props.children}
        </label>
      </div>
    );
  },
);

interface RadioGroupProps {
  options: Record<string, string>;
  name: {
    [key in keyof TSFormSchema]: TSFormSchema[key] extends string[]
      ? key
      : never;
  }[keyof TSFormSchema];
}

const RadioGroup = observer(
  (props: RadioGroupProps): React.ReactElement => {
    const state = useContext(FormContext);
    const formValue = state?.values[props.name];

    return (
      <>
        {Object.entries(props.options).map(([value, label]) => (
          <label key={value} class="radio-inline">
            <input
              type="radio"
              value={value}
              checked={value === formValue}
              onChange={action((e: React.ChangeEvent<HTMLInputElement>) => {
                if (state && e.target.checked) {
                  state.values[props.name] = value;
                }
              })}
            />
            {label}
          </label>
        ))}
      </>
    );
  },
);
        

interface FormProps {
  children: React.ReactNode[] | React.ReactNode;
  defaultValues: TSFormSchema;
}

const Form = function (props: FormProps): React.ReactElement {
  const [state] = useState(() => new FormState(props.defaultValues));

  return (
    <FormContext.Provider value={state}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          state.submit();
        }}>
        {props.children}
      </form>
    </FormContext.Provider>
  );
};

const StyledForm = styled(Form)`
  position: relative;
`;

const LoadingOverlay = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.3);
  cursor: progress;
  display: ${props => props.active ? 'block' : 'none'};
`;

interface MainCompState {
  loading: boolean;
  error: boolean;
  prospekte: string[];
}

export default (): React.ReactElement => {
  const [state, setState] = useState<MainCompState>({
    loading: true,
    error: false,
    prospekte: [],
  });

  useEffect(() => {
    void (async () => {
      let response: Response;
      let result: string[];

      try {
        response = await fetch('/prospekt-api/prospekte');
        result = (await response.json()) as string[];
      } catch (err) {
        console.error(err);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: true,
        }));
        return;
      }

      if (!Array.isArray(result)) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: true,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          loading: false,
          prospekte: result,
        }));
      }
    })();
  }, []);

  return (
    <Form
      defaultValues={{
        anrede: '',
        name: '',
        street: '',
        zip: '',
        prospekte: [],
      }}>
      <RadioGroup name="anrede" label="Anrede" options={{Frau: 'Frau', Herr: 'Herr', ' ': 'Keine Angabe'}} />
      <FormField name="name" label="Ihr Name" />
      <FormField name="street" label="Straße, Hausnummer" />
      <FormField name="zip" label="PLZ, Wohnort" />
      <div className="form-group">
        <label>Prospekte:</label>
        {state.loading
          ? 'Lade...'
          : state.error
          ? 'Fehler'
          : state.prospekte.map((name) => (
              <Checkbox key={name} name="prospekte" value={name}>
                {name}
              </Checkbox>
            ))}
      </div>
      <button type="submit" className="btn btn-primary">Bestellen</button>
      <LoadingOverlay active={state.loading} />
    </Form>
  );
};
